import axios, { AxiosResponse, isAxiosError } from "axios";
import { Paginated, RequestError, setFile as sendFile } from "./api";

const url = `${process.env.REACT_APP_URL}/simulados`;

export interface Simulado {
  id: number;
  idBarragem: number;
  status: boolean;
  tipo?: string;
  data: Date;
  responsavel: string;
  populacao?: number;
  obs?: string;
  dataCadastro: Date;
  dataModificacao?: Date;
  idUpdate: string;
  file?: string;
  file_size?: number;
  anterior?: Simulado;
}

const add = async (
  idBarragem: number,
  simulado: Simulado
): Promise<Simulado | RequestError> => {
  try {
    const data: AxiosResponse<Simulado> = await axios.post(
      `${url}/${idBarragem}`,
      {
        ...simulado,
        data: simulado.data.toISOString(),
      }
    );

    return processData(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Treinamento Error",
        message:
          error.response?.data.message ?? "Erro ao adicionar este simulado",
      };
    } else {
      throw error;
    }
  }
};

const getAll = async (
  idBarragem: number,
  page: number,
  perPage: number
): Promise<Paginated<Simulado> | RequestError> => {
  try {
    const data: AxiosResponse<Paginated<Simulado>> = await axios.get(
      `${url}/all/${idBarragem}/${page}/${perPage}`
    );

    data.data.items = data.data.items.map(processData);

    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Treinamento Error",
        message: error.response?.data.message ?? "Erro ao obter simulado",
      };
    } else {
      throw error;
    }
  }
};

const getTrash = async (
  idBarragem: number,
  page: number,
  perPage: number
): Promise<Paginated<Simulado> | RequestError> => {
  try {
    const data: AxiosResponse<Paginated<Simulado>> = await axios.get(
      `${url}/trash/${idBarragem}/${page}/${perPage}`
    );

    data.data.items = data.data.items.map(processData);

    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Treinamento Error",
        message: error.response?.data.message ?? "Erro ao obter simulado",
      };
    } else {
      throw error;
    }
  }
};

const getById = async (
  idSimulado: number
): Promise<Simulado | RequestError> => {
  try {
    const data: AxiosResponse<Simulado> = await axios.get(
      `${url}/${idSimulado}`
    );

    return processData(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Treinamento Error",
        message: error.response?.data.message ?? "Erro ao obter simulado",
      };
    } else {
      throw error;
    }
  }
};

const update = async (
  simulado: Simulado,
  file?: File,
  onProgess?: (percent: number) => void,
): Promise<Simulado | RequestError> => {

  try {
    const data: AxiosResponse<Simulado> = await axios.put(
      `${url}/${simulado.id}`,
      {
        ...simulado,
        data: simulado.data.toISOString(),
      }
    );

    const obj = processData(data.data);
    if ("error" in obj) return obj;

    if (file) {
      const fileAdd = setFile(obj.id, file, onProgess);
      if ("error" in fileAdd) return fileAdd;
    }

    return obj;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Treinamento Error",
        message:
          error.response?.data.message ?? "Erro ao atualizar simulado",
      };
    } else {
      throw error;
    }
  }
};

const setFile = async (
  idSimulado: number,
  file: File,
  onProgess?: (percent: number) => void,
): Promise<Simulado | RequestError> => {
  return sendFile<Simulado>(
    `${url}/file_upload_parts/${idSimulado}`,
    `${url}/file_upload_complete/${idSimulado}`,
    file, onProgess,
  );
};

const remove = async (
  idSimulado: number
): Promise<Simulado | RequestError> => {
  try {
    const data: AxiosResponse<Simulado> = await axios.delete(
      `${url}/${idSimulado}`
    );

    return processData(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Treinamento Error",
        message:
          error.response?.data.message ?? "Erro ao atualizar simulado",
      };
    } else {
      throw error;
    }
  }
};

const restore = async (
  idSimulado: number
): Promise<Simulado | RequestError> => {
  try {
    const data: AxiosResponse<Simulado> = await axios.put(
      `${url}/restore/${idSimulado}`
    );

    return processData(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Treinamento Error",
        message:
          error.response?.data.message ?? "Erro ao atualizar simulado",
      };
    } else {
      throw error;
    }
  }
};

function processData(value: Simulado) {
  value.data = new Date(value.data);
  value.dataCadastro = new Date(value.dataCadastro);
  value.dataModificacao &&= new Date(value.dataCadastro);
  if (value.anterior) {
    value.anterior.data = new Date(value.anterior.data);
    value.anterior.dataCadastro = new Date(value.anterior.dataCadastro);
    value.anterior.dataModificacao &&= new Date(value.anterior.dataCadastro);
  }

  return value;
}

export const ApiSimulado = {
  add,
  remove,
  update,
  getAll,
  getById,
  setFile,
  restore,
  getTrash,
};
